import { matchPath, useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Divider,
  Flex,
  H5,
  Notification,
  Small,
} from "orcs-design-system";

import PATHS, { url } from "src/consts/urlPaths";
import InboxList from "src/inbox/InboxList";
import { arrivedFromInterstitial } from "src/inbox/inboxTransition";
import {
  filtersFromSearch,
  inboxFilterCaption,
  inboxFilterLabels,
} from "src/inbox/kinds";
import { INBOX_PAGE_SIZE, useInboxItems } from "src/inbox/useInboxItems";
import { useInboxTeamName } from "src/inbox/useInboxTeamName";
import { useInboxWaitingCount } from "src/inbox/useInboxWaitingCount";

/** Highlight the open item. `/inbox` with no id shows the landing, so none. */
export const selectedInboxKey = (pathname: string): string | null =>
  matchPath<{ itemId?: string }>(pathname, {
    path: PATHS.WORKFORCE_INBOX,
  })?.params.itemId ?? null;

const InboxContent = () => {
  const history = useHistory();
  const { pathname, search, state } = useLocation();
  const { category, routeId, teamId } = filtersFromSearch(search);
  const {
    items,
    total,
    routes,
    loading,
    error,
    hasMore,
    loadingMore,
    loadMore,
    categoryPending,
  } = useInboxItems(true, {
    category,
    routeId,
    teamId,
    pageSize: INBOX_PAGE_SIZE,
  });
  const waitingCount = useInboxWaitingCount(true);
  const teamName = useInboxTeamName(teamId);
  const selectedKey = selectedInboxKey(pathname);
  const arrivedKey = arrivedFromInterstitial(state) ? selectedKey : null;
  const itemsLoading =
    (loading || categoryPending) && items.length === 0 && !error;
  const showError = !itemsLoading && Boolean(error) && items.length === 0;
  const { categoryLabel, routeLabel, teamLabel } = inboxFilterLabels({
    category,
    routeId,
    routes,
    teamLabel: teamName,
  });
  const caption =
    itemsLoading && total === 0
      ? null
      : inboxFilterCaption({
          categoryLabel,
          routeLabel,
          teamLabel,
          total,
          waiting: waitingCount ?? total,
        });

  return (
    // Block-level, not a flex container: the panel's close button is floated
    // right, and a flex BFC will not overlap it — the whole inbox column
    // would stay narrowed for the full panel height. Same as Dashboards.
    // Absolute fill is inside this box, so it is full width; the float only
    // overlaps the in-flow title row.
    <Box height="100%" position="relative">
      <Flex
        position="absolute"
        top={0}
        right={0}
        bottom={0}
        left={0}
        flexDirection="column"
        minHeight={0}
        overflow="hidden"
      >
        <H5 fontWeight="bold">Inbox</H5>
        <Box my="r">
          <Divider light />
        </Box>
        {caption ? (
          <Box mb="s">
            <Small color="subText">{caption}</Small>
          </Box>
        ) : null}
        {showError ? (
          <Notification variant="error" closable={false}>
            The inbox could not be loaded.
          </Notification>
        ) : null}
        {!showError ? (
          <Box flex="1 1 auto" minHeight={0} overflow="hidden">
            <InboxList
              items={items}
              selectedKey={selectedKey}
              arrivedKey={arrivedKey}
              otherCount={
                category === "EVERYTHING" && !routeId && !teamId
                  ? 0
                  : Math.max(0, (waitingCount ?? 0) - total)
              }
              hasMore={hasMore}
              loadingMore={loadingMore}
              itemsLoading={itemsLoading}
              onSelect={(key) => {
                history.push(
                  url(PATHS.WORKFORCE_INBOX, { itemId: key }) + search
                );
              }}
              onLoadMore={loadMore}
            />
          </Box>
        ) : null}
      </Flex>
    </Box>
  );
};

export default InboxContent;
